import React, { useEffect, useContext, useMemo } from 'react';
import { object, node } from 'prop-types';
import QueryString from 'querystring';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import useSessionStorage from '@lmig/dotcom-aspect-hooks/useSessionStorage';
import { ContextApp } from './ContextApp';

const logger = new Logger('client:context:ContextParameter');

const filterParameters = params => {
  // Whitelist is built from research documented here:
  // https://forge.lmig.com/wiki/pages/viewpage.action?spaceKey=USCMDP&title=URL+Parameters
  // and combined with values documented for the aggregator API as documented here:
  // https://forge.lmig.com/wiki/display/GRMInternet/Ostium+API
  const WHITELIST = [
    // Indicates the visitor came from the /affinity-portal page. Not sure if other uses.
    'affPortal',
    // Indicates any LOB's that are restricted for the affinity currently in session
    'aff_res',
    // the alias of an affinity or sales rep
    'alias',
    // the blog category to display (new Dec 2013)
    'CAT',
    'cmpgncde', // Campaign Code for a marketing/affinity campaign
    'campaignCode',
    // Exact Target email specific creative tracking codes used by analytics
    'et_cid',
    'et_rid',
    // not sure what it means but believe quot app requires & 'event=continue'
    // seems to be the only value lm.com uses
    'event',
    'int', // Omniture; indication of referring link location, possibly being retired
    'keyCode', // Tracking Code assigned by Interactive Marketing
    'landingPage', // for quoting app only, not sure what it means but only value seems to be 'True'
    'lob', // Line of Business for quoting as selected by user; values are Auto or Property
    // the user's identifier for specific affinity relationships, including certain airlines/hotels
    // & 'Adharmonics' campaign; assigned by 3rd party
    'MemberID',
    'MM_webID', // Affinity or Sales Rep webid
    'r_webID', // webid of the Sales Rep ID IF both Affinity & Rep in session
    'movie', // indicates the video that should be selected within the media center when the page loads
    'officenumber', // one method of linking to a specific office's lm.com page
    // Mail & Email Tracking code; represents a household "Personal Invitation Number" policyType for Quoting;
    // further defines line of business based on user selection.per CAR:
    // H3 homeowner
    // H4 renters
    // H6 condo
    // H7 auto
    'PIN',
    // retired? not sure but may have been used for quoting at one time.
    // Per CAR, values could be AutoSimple or PropSimple
    'product',
    // Indicates marketing campaign/source of traffic
    'src',
    'srcCode',
    'trackingSrcCode',
    // System will populate with the sales rep's state IF a sales rep is in session whose primary
    // office is MA or TX.Not sure what other / inbound scenarios might be ?
    'state',
    'udcc', // Mobile Device full site / mobile site preference indicator
    // 1. Used for inbound rep search/results display; also used for within-site rep search/results
    // 2. For quote app, represents user-entered zip in a quote module, although quote app might
    // instead use zipCode which has same value
    // 1. Selected pages can use the zip to generate results
    // Ex:/start.do?lob=Auto&event=continue&landingPage=true&policyType=H7&zipCode=02144&zip=02144
    'zip',
    'zipCode',
    'clickid',
    'insureds',
    // Used to denote when a quote lead comes from one of our /state pages.
    'jurisdictionPage'
  ];

  return Object.entries(params).reduce((acc, [key, value]) => {
    if (WHITELIST.includes(key)) {
      return {
        ...acc,
        [key]: value,
      };
    }

    return acc;
  }, {});
};

const buildParametersFromContext = entities => Object.keys(entities).reduce(
  // eslint-disable-next-line complexity
  (acc, stateKey) => {
    const { queryType, queryValue, webId } = entities[stateKey];

    if (!queryValue) {
      return acc;
    }

    if (!queryType) {
      logger.warn(`No queryType for queryValue: ${queryValue}`);
    }

    let urlQueryType = '';
    let urlQueryValue = queryValue;

    switch (stateKey) {
      case 'agent':
        if (queryType === 'alias') {
          urlQueryValue = webId;
        }

        urlQueryType = 'r_webID';
        break;
      case 'campaign':
        urlQueryType = 'cmpgncde';
        break;
      case 'affinity':
        if (queryType === 'alias') {
          urlQueryValue = webId;
        }

        urlQueryType = 'MM_webID';
        break;
      case 'office':
        break;
      default:
        logger.warn(`Unexpected entity type encountered: ${stateKey}`);
        break;
    }

    return {
      ...acc,
      [urlQueryType]: urlQueryValue,
    };
  },
  {},
);

const ContextParameter = React.createContext();

// ParameterContextProvider is used to hold parameters which could be appended to links
//   point to Quoting (eSales) and Login (eService). They can come from either "legacy" LSDS
//   comSession cookie or as query parameters on URL.
const ParameterContextProvider = ({ children, value = {} }) => {
  const appContextValues = useContext(ContextApp);

  if (!appContextValues) {
    throw new Error('ContextParameter must be inside <AppContextProvider />');
  }

  const { entities } = appContextValues;
  const [currentValues, setCurrentValues] = useSessionStorage('aspect-link-append-parameters', {
    ...buildParametersFromContext(entities),
    ...value,
  });

  // When entities change (e.g. Navigating through the application) keep the
  // ContextParameter values up to date.
  useEffect(() => {
    const newParams = buildParametersFromContext(entities);
    logger.debug(`Adding new params from entities ${JSON.stringify(newParams)}`);

    setCurrentValues(values => ({
      ...values,
      ...newParams,
    }));
  }, [entities, setCurrentValues]);

  // When the component mounts in the browser, we parse all the query parameters
  //   on the URL and add them to the Parameter values.
  useEffect(() => {
    const paramString = window.location.search.slice(1);

    try {
      const paramObject = QueryString.parse(paramString);

      logger.debug(`Adding params: ${JSON.stringify(paramObject)}`);

      setCurrentValues(values => ({
        ...values,
        ...filterParameters(paramObject),
      }));
    } catch (e) {
      logger.error(`Unable to parse URL parameters: ${paramString} - ${e.message}`);
    }
  }, [setCurrentValues]);

  const memoValue = useMemo(() => ({
    values: filterParameters(currentValues),
  }), [currentValues]);

  return (
    <ContextParameter.Provider value={memoValue}>
      {children}
    </ContextParameter.Provider>
  );
};

ParameterContextProvider.propTypes = {
  children: node.isRequired,
  value: object, // eslint-disable-line
};


export { ContextParameter, ParameterContextProvider };
